/**
 * Mi Agenda Personal — vista tipo Google Calendar.
 * Cualquier tipo de item: visita_cliente · plomero · montaje · reunion · personal · pago · compras · etc.
 * Notificaciones automáticas cuando el recordatorio venza (polling cada 60s).
 */
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import {
  Calendar, ChevronLeft, ChevronRight, Plus, Trash2, MapPin, Users,
  Bell, Check, Clock, CalendarDays, X
} from "lucide-react";

// ── Utilidades de fecha ──
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const DIAS_SEMANA = ["D", "L", "M", "M", "J", "V", "S"];
const toISO = (d) => d.toISOString().slice(0, 10);
const parseISO = (s) => new Date(`${s}T12:00:00`);
const hoyISO = () => toISO(new Date());

const TIPOS_DEFAULT = [
  { key: "visita_cliente", label: "Visita cliente", color: "#e11d48" },
  { key: "plomero",        label: "Plomero",       color: "#f59e0b" },
  { key: "electricista",   label: "Electricista",  color: "#f59e0b" },
  { key: "montaje",        label: "Montaje",       color: "#4f46e5" },
  { key: "desmontaje",     label: "Desmontaje",    color: "#4f46e5" },
  { key: "reunion",        label: "Reunión",       color: "#059669" },
  { key: "personal",       label: "Personal",      color: "#6b7280" },
  { key: "entrega",        label: "Entrega",       color: "#0891b2" },
  { key: "compras",        label: "Compras",       color: "#7c3aed" },
  { key: "pago",           label: "Pago",          color: "#d97706" },
  { key: "evento",         label: "Evento",        color: "#be185d" },
  { key: "otro",           label: "Otro",          color: "#78716c" },
];

const FORM_VACIO = {
  agenda_id: null,
  tipo: "visita_cliente",
  titulo: "",
  descripcion: "",
  fecha: hoyISO(),
  hora_inicio: "09:00",
  hora_fin: "",
  todo_el_dia: false,
  ubicacion: "",
  personas: [], // {nombre, telefono, rol}
  recordatorio_min_antes: 30,
  notificar_por: ["in_app"],
};

const MiAgendaPersonal = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Iter124 · P0 · roles solo-lectura-asignada no pueden crear/editar/borrar agenda personal
  const esRolSoloLectura = ["mesero", "chef", "cliente"].includes((user?.workspace_rol || "").toLowerCase());
  const [mes, setMes] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [diaSel, setDiaSel] = useState(hoyISO());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const notificados = useRef(new Set()); // en memoria durante la sesión

  const primerDia = new Date(mes.y, mes.m, 1);
  const ultimoDia = new Date(mes.y, mes.m + 1, 0);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      // Traer el mes completo + 7 días extras
      const desde = new Date(mes.y, mes.m - 1, 25);
      const hasta = new Date(mes.y, mes.m + 1, 7);
      const { data } = await axios.get(`${API}/agenda?desde=${toISO(desde)}&hasta=${toISO(hasta)}&incluir_completados=true`);
      setItems(data || []);
    } catch {
      toast.error("Error al cargar la agenda");
    } finally {
      setLoading(false);
    }
  }, [mes.y, mes.m]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Polling de notificaciones cada 60s
  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/agenda/pendientes-notificacion`);
        for (const it of (data || [])) {
          if (notificados.current.has(it.agenda_id)) continue;
          notificados.current.add(it.agenda_id);
          toast.info(`🔔 ${it.titulo}`, {
            description: `${it.fecha} · ${it.hora_inicio}${it.ubicacion ? ` · ${it.ubicacion}` : ""}`,
            duration: 15000,
            action: {
              label: "Visto",
              onClick: async () => {
                try { await axios.post(`${API}/agenda/${it.agenda_id}/marcar-notificado`); } catch { /* noop */ }
              },
            },
          });
          // Notificación del navegador si el user aceptó permisos
          if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            try { new Notification(it.titulo, { body: `${it.fecha} ${it.hora_inicio}${it.ubicacion ? " · " + it.ubicacion : ""}` }); } catch { /* noop */ }
          }
          // Marcar en el server para que no vuelva a salir
          axios.post(`${API}/agenda/${it.agenda_id}/marcar-notificado`).catch(() => { /* noop */ });
        }
      } catch { /* noop */ }
    };
    check(); // inmediato
    const iv = setInterval(check, 60000);
    return () => clearInterval(iv);
  }, []);

  // Pedir permisos de notificaciones si aún no
  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch { /* noop */ }
    }
  }, []);

  const itemsPorFecha = useMemo(() => {
    const map = {};
    for (const it of items) {
      if (!map[it.fecha]) map[it.fecha] = [];
      map[it.fecha].push(it);
    }
    return map;
  }, [items]);

  const itemsDiaSel = (itemsPorFecha[diaSel] || []).sort((a, b) => (a.hora_inicio || "").localeCompare(b.hora_inicio || ""));

  const grid = useMemo(() => {
    const arr = [];
    const startOffset = primerDia.getDay(); // 0=domingo
    for (let i = 0; i < startOffset; i++) arr.push(null);
    for (let d = 1; d <= ultimoDia.getDate(); d++) arr.push(new Date(mes.y, mes.m, d));
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [mes, primerDia, ultimoDia]);

  const cambiarMes = (delta) => {
    setMes((prev) => {
      const nm = prev.m + delta;
      if (nm < 0) return { y: prev.y - 1, m: 11 };
      if (nm > 11) return { y: prev.y + 1, m: 0 };
      return { ...prev, m: nm };
    });
  };

  const abrirCrear = () => { setForm({ ...FORM_VACIO, fecha: diaSel }); setDialogOpen(true); };
  const abrirEditar = (it) => {
    setForm({
      ...FORM_VACIO,
      ...it,
      personas: it.personas || [],
      notificar_por: it.notificar_por || ["in_app"],
    });
    setDialogOpen(true);
  };

  const guardar = async () => {
    if (!form.titulo.trim()) { toast.error("El título es requerido"); return; }
    setSaving(true);
    try {
      if (form.agenda_id) {
        await axios.put(`${API}/agenda/${form.agenda_id}`, form);
        toast.success("Actualizado");
      } else {
        await axios.post(`${API}/agenda`, form);
        toast.success("Creado");
      }
      setDialogOpen(false);
      fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const eliminar = async (id) => {
    if (id.startsWith("__ev_")) { toast.info("Los eventos se editan desde el módulo de Eventos"); return; }
    if (!window.confirm("¿Eliminar este ítem de la agenda?")) return;
    try {
      await axios.delete(`${API}/agenda/${id}`);
      toast.success("Eliminado");
      fetchItems();
    } catch { toast.error("Error"); }
  };

  const toggleCompletado = async (id) => {
    if (id.startsWith("__ev_")) { toast.info("Estado del evento se cambia desde el módulo de Eventos"); return; }
    try {
      await axios.post(`${API}/agenda/${id}/marcar-completado`);
      fetchItems();
    } catch { toast.error("Error"); }
  };

  const addPersona = () => setForm(f => ({ ...f, personas: [...(f.personas || []), { nombre: "", telefono: "", rol: "" }] }));
  const setPersona = (i, patch) => setForm(f => {
    const arr = [...(f.personas || [])];
    arr[i] = { ...arr[i], ...patch };
    return { ...f, personas: arr };
  });
  const delPersona = (i) => setForm(f => ({ ...f, personas: (f.personas || []).filter((_, x) => x !== i) }));

  return (
    <div className="space-y-4 pb-24" data-testid="mi-agenda-personal">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl text-stone-900 flex items-center gap-2">
            <CalendarDays className="w-7 h-7 text-emerald-700" />
            Mi Agenda
          </h1>
          <p className="text-sm text-stone-600">Tu asistente personal — cualquier tipo de evento con recordatorios.</p>
        </div>
        <Button onClick={abrirCrear} className="bg-emerald-900 hover:bg-emerald-800 text-white" data-testid="btn-nuevo-item" style={esRolSoloLectura ? { display: 'none' } : undefined}>
          <Plus className="w-4 h-4 mr-1" /> Nuevo
        </Button>
      </div>

      {/* Iter101 · Leyenda solo para master. Los tenants ven leyenda genérica. */}
      <div className="flex flex-wrap gap-3 text-xs" data-testid="agenda-leyenda">
        <span className="flex items-center gap-1 text-stone-500">Colores por unidad de negocio · Otros tipos: personal · plomero · montaje · etc.</span>
      </div>

      {/* Navegación mes */}
      <Card className="border-stone-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <Button variant="ghost" size="sm" onClick={() => cambiarMes(-1)} data-testid="btn-mes-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <h2 className="font-display text-lg" data-testid="mes-actual">{MESES[mes.m]} {mes.y}</h2>
            <Button variant="ghost" size="sm" onClick={() => cambiarMes(1)} data-testid="btn-mes-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>

          {/* Calendario */}
          <div className="grid grid-cols-7 gap-1 text-center text-xs text-stone-500 mb-1">
            {DIAS_SEMANA.map((d, i) => <div key={i}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {grid.map((d, i) => {
              if (!d) return <div key={i} />;
              const iso = toISO(d);
              const itemsHoy = itemsPorFecha[iso] || [];
              const esHoy = iso === hoyISO();
              const esSel = iso === diaSel;
              return (
                <button
                  key={i}
                  onClick={() => setDiaSel(iso)}
                  className={`aspect-square rounded-lg border text-xs p-1 flex flex-col items-center justify-start transition
                    ${esSel ? "bg-emerald-100 border-emerald-500" : "border-stone-200 hover:border-emerald-300"}
                    ${esHoy && !esSel ? "bg-amber-50 border-amber-300" : ""}`}
                  data-testid={`dia-${iso}`}
                >
                  <span className={`font-medium ${esHoy ? "text-amber-700" : "text-stone-800"}`}>{d.getDate()}</span>
                  <div className="flex flex-wrap justify-center gap-0.5 mt-0.5">
                    {itemsHoy.slice(0, 4).map((it, j) => (
                      <span key={j} className="w-1.5 h-1.5 rounded-full" style={{ background: it.color || "#78716c" }} />
                    ))}
                    {itemsHoy.length > 4 && <span className="text-[9px] text-stone-500">+</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Lista día seleccionado */}
      <Card className="border-stone-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <h3 className="font-display text-base text-stone-900 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-emerald-700" />
              {parseISO(diaSel).toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" })}
            </h3>
            <span className="text-xs text-stone-500">{itemsDiaSel.length} items</span>
          </div>
          {loading ? (
            <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" /></div>
          ) : itemsDiaSel.length === 0 ? (
            <div className="text-center py-8 text-stone-500">
              <Bell className="w-8 h-8 mx-auto mb-2 text-stone-300" />
              <p className="text-sm">Sin actividades este día.</p>
              <Button variant="link" onClick={abrirCrear} className="text-emerald-700 mt-2">Agregar</Button>
            </div>
          ) : (
            <div className="space-y-2">
              {itemsDiaSel.map(it => {
                const esEvento = it.es_evento || (it.agenda_id || "").startsWith("__ev_");
                return (
                <div key={it.agenda_id} className="border border-stone-200 rounded-lg p-3 flex items-start gap-2" data-testid={`item-${it.agenda_id}`}
                  style={{ borderLeft: `4px solid ${it.color || "#78716c"}` }}>
                  {esEvento ? (
                    <div className="mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0" title="Evento (readonly)">
                      <Calendar className="w-4 h-4 text-purple-700" />
                    </div>
                  ) : (
                    <button onClick={() => toggleCompletado(it.agenda_id)} className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0
                      ${it.estado === "completado" ? "bg-emerald-600 border-emerald-600" : "border-stone-300"}`} data-testid={`check-${it.agenda_id}`}>
                      {it.estado === "completado" && <Check className="w-3 h-3 text-white" />}
                    </button>
                  )}
                  <div className="flex-1 min-w-0 cursor-pointer"
                       onClick={() => esEvento ? navigate(`/panel/eventos/${it.evento_id}`) : (esRolSoloLectura ? null : abrirEditar(it))}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-medium ${it.estado === "completado" ? "line-through text-stone-500" : "text-stone-900"}`}>{it.titulo}</span>
                      {it.es_integral ? (
                        <span className="text-[10px] uppercase px-1.5 py-0.5 rounded font-bold" style={{ background: "#7c3aed20", color: "#7c3aed" }}>
                          ⭐ Integral · {(it.unidades || []).length} unidades
                        </span>
                      ) : esEvento ? (
                        <span className="text-[10px] uppercase px-1.5 py-0.5 rounded" style={{ background: `${it.color}20`, color: it.color }}>
                          {(it.unidades && it.unidades[0]) || "evento"}
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase px-1.5 py-0.5 rounded" style={{ background: `${it.color}20`, color: it.color }}>{it.tipo}</span>
                      )}
                    </div>
                    <div className="text-xs text-stone-500 flex items-center gap-3 flex-wrap mt-0.5">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {it.hora_inicio}{it.hora_fin && ` – ${it.hora_fin}`}</span>
                      {it.ubicacion && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {it.ubicacion}</span>}
                      {it.personas?.length > 0 && <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {it.personas.length}</span>}
                      {it.recordatorio_min_antes > 0 && <span className="flex items-center gap-1 text-amber-700"><Bell className="w-3 h-3" /> {it.recordatorio_min_antes}m antes</span>}
                    </div>
                    {it.descripcion && <p className="text-xs text-stone-600 mt-1">{it.descripcion}</p>}
                  </div>
                  {!esEvento && !esRolSoloLectura && (
                    <button onClick={() => eliminar(it.agenda_id)} className="text-red-500 hover:bg-red-50 p-1 rounded" data-testid={`del-${it.agenda_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );})}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal crear/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg w-[calc(100vw-1rem)] max-h-[92vh] p-0 flex flex-col overflow-hidden" data-testid="dialog-agenda">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-stone-200 flex-shrink-0">
            <DialogTitle className="font-display text-xl">{form.agenda_id ? "Editar" : "Nuevo"} en agenda</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4 px-5 flex-1 overflow-y-auto">
            <div>
              <Label>Título *</Label>
              <Input value={form.titulo} onChange={(e) => setForm(f => ({ ...f, titulo: e.target.value }))} placeholder="Visita Fátima 3pm" data-testid="input-titulo" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo</Label>
                <select value={form.tipo} onChange={(e) => setForm(f => ({ ...f, tipo: e.target.value, color: TIPOS_DEFAULT.find(t => t.key === e.target.value)?.color || f.color }))} className="w-full border border-stone-300 rounded-md px-2 py-2 text-sm" data-testid="sel-tipo">
                  {TIPOS_DEFAULT.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <Label>Fecha *</Label>
                <Input type="date" value={form.fecha} onChange={(e) => setForm(f => ({ ...f, fecha: e.target.value }))} data-testid="input-fecha" />
              </div>
              <div>
                <Label>Hora inicio</Label>
                <Input type="time" value={form.hora_inicio} onChange={(e) => setForm(f => ({ ...f, hora_inicio: e.target.value }))} data-testid="input-hora-inicio" />
              </div>
              <div>
                <Label>Hora fin (opcional)</Label>
                <Input type="time" value={form.hora_fin || ""} onChange={(e) => setForm(f => ({ ...f, hora_fin: e.target.value }))} data-testid="input-hora-fin" />
              </div>
            </div>
            <div>
              <Label>Ubicación</Label>
              <Input value={form.ubicacion || ""} onChange={(e) => setForm(f => ({ ...f, ubicacion: e.target.value }))} placeholder="Dirección o ubicación del evento…" data-testid="input-ubicacion" />
            </div>
            <div>
              <Label>Recordarme (minutos antes)</Label>
              <select value={form.recordatorio_min_antes} onChange={(e) => setForm(f => ({ ...f, recordatorio_min_antes: Number(e.target.value) }))} className="w-full border border-stone-300 rounded-md px-2 py-2 text-sm" data-testid="sel-recordatorio">
                <option value={0}>Sin recordatorio</option>
                <option value={5}>5 min antes</option>
                <option value={15}>15 min antes</option>
                <option value={30}>30 min antes</option>
                <option value={60}>1 hora antes</option>
                <option value={120}>2 horas antes</option>
                <option value={1440}>1 día antes</option>
              </select>
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea value={form.descripcion || ""} onChange={(e) => setForm(f => ({ ...f, descripcion: e.target.value }))} rows={2} placeholder="Detalles…" data-testid="input-desc" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Personas involucradas</Label>
                <Button size="sm" variant="outline" onClick={addPersona} data-testid="btn-add-persona"><Plus className="w-3 h-3 mr-1" />Agregar</Button>
              </div>
              {(form.personas || []).map((p, i) => (
                <div key={i} className="flex gap-1 mb-1" data-testid={`row-persona-${i}`}>
                  <Input value={p.nombre || ""} onChange={(e) => setPersona(i, { nombre: e.target.value })} placeholder="Nombre" className="flex-1 text-sm" />
                  <Input value={p.telefono || ""} onChange={(e) => setPersona(i, { telefono: e.target.value })} placeholder="Tel" className="w-28 text-sm" />
                  <Input value={p.rol || ""} onChange={(e) => setPersona(i, { rol: e.target.value })} placeholder="Rol" className="w-24 text-sm" />
                  <button onClick={() => delPersona(i)} className="text-red-500 p-2"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="px-5 py-3 border-t border-stone-200 bg-white flex-shrink-0 gap-2 sm:gap-0 flex-col-reverse sm:flex-row">
            {form.agenda_id && (
              <Button variant="outline" onClick={() => { eliminar(form.agenda_id); setDialogOpen(false); }} className="text-red-600 border-red-300 w-full sm:w-auto sm:mr-auto" data-testid="btn-del-modal">Eliminar</Button>
            )}
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="w-full sm:w-auto">Cancelar</Button>
            <Button onClick={guardar} disabled={saving} className="bg-emerald-900 hover:bg-emerald-800 text-white w-full sm:w-auto" data-testid="btn-guardar-agenda">
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FAB móvil */}
      {!esRolSoloLectura && (
        <button
          onClick={abrirCrear}
          className="sm:hidden fixed bottom-6 right-6 w-14 h-14 rounded-full bg-emerald-900 text-white shadow-lg flex items-center justify-center z-40"
          data-testid="btn-fab-nuevo"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};

export default MiAgendaPersonal;
